import React, { ReactNode, ElementType, ReactElement } from 'react'
import { Box, Flex, ThemeUIStyleObject } from 'theme-ui'
import HeaderNavMenuButtonMobile from 'components/Dashboard/NavBar/HeaderNavMenuButton.mobile'
import { Button, Svg } from 'ui'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import TextWithOverFlowAndTippyPopup from 'components/TextWithOverFlowAndTippyPopup'
import InfoIcon from 'images/dashboard/info-icon.svg'
import useTutorialSlides from 'components/Dashboard/utils/useTutorialSlides'
import useGetUser from 'components/Dashboard/utils/useGetUser'
import { TutorialType } from 'store/tutorial/tutorial'
import { useAppSelector } from 'store/hooks'
import { keyframes } from '@emotion/react'

export interface DashboardHeaderProps {
  children?: ReactNode
  icon?: ElementType
  rightContent?: ReactNode
  title?: string
  titleContent?: ReactNode // when we need to use the specific content instead of title
  description?: string | ReactNode
  sx?: ThemeUIStyleObject
  tutorialSlide?: number
  customTutorial?: TutorialType
}

export default function DashboardHeader({
  children,
  icon,
  title,
  titleContent,
  description,
  rightContent,
  sx,
  tutorialSlide,
  customTutorial,
}: DashboardHeaderProps): ReactElement {
  const isMobile = useBreakpointIndex() === 0
  const { data: userData } = useGetUser()
  const userPk = userData?.PK || ''
  const sessionTutorials = userData?.tutorials
  const shouldShowDescription = description && !!userPk && !sessionTutorials
  const isInAnalyticsMaintenanceMode = useAppSelector(s => s.errors.isInAnalyticsMaintenanceMode)
  const isViewingCustomDateTimePicker = useAppSelector(
    s => s.analytics.isViewingCustomDateTimePicker,
  )

  return (
    <Flex
      data-testid="dashboard-header"
      sx={{
        backgroundColor: 'none',
        position: 'relative',
        width: '100%',
        borderBottom: ['1px solid', 'none'],
        borderBottomColor: 'white10',
        height: '5.5rem',
        justifyContent: 'space-between',
        pl: ['1.2rem', 0],
        pr: ['1.2rem', '1.6rem'],
        pt: ['0.8rem', '1.6rem'],
        mb: ['0.8rem', '2.4rem'],
        ...sx,
      }}
      // date picker should have the full width on mobile
      className={
        isMobile && isViewingCustomDateTimePicker
          ? 'reset-tippy-width centered-header-tippy mobile'
          : ''
      }
    >
      <Flex
        sx={{
          alignItems: 'center',
          gap: ['0.8rem', '1.2rem'],
        }}
      >
        <HeaderNavMenuButtonMobile />
        {icon && (
          <Flex
            sx={{
              width: ['2.4rem', '3.8rem'],
              height: ['2.4rem', '3.8rem'],
              justifyContent: 'center',
              alignItems: 'center',
              border: '1px solid',
              borderColor: ['transparent', 'blueYonder30'],
              borderRadius: '1.2rem',
              flexShrink: 0,
            }}
          >
            <Svg
              svg={icon}
              fill="aliceBlue"
              sx={{ height: '2.4rem', width: '2.4rem', flexShrink: 0, '& g': { opacity: 1 } }}
              descriptiveText={`${title} section icon`}
            />
          </Flex>
        )}
        <Flex
          sx={{
            mr: '1.2rem',
            alignItems: ['baseline', 'center', 'center'],
            flexDirection: ['column', 'row'],
            flexGrow: 1,
          }}
        >
          <Flex
            sx={{
              width: '100%',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <Flex
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                flexDirection: ['column', 'row'],
                mb: [0, '0.4rem', '0.4rem'],
              }}
            >
              <Flex sx={{ alignItems: 'center', gap: ['0.4rem', 0], minWidth: '8rem' }}>
                {titleContent ||
                  (title && (
                    <TextWithOverFlowAndTippyPopup
                      variant={['size15Weight700', 'size17Weight700Line138']}
                      testId="header-title"
                      content={title}
                      ariaLabel={title}
                      placement="bottom-start"
                      sxText={{
                        minWidth: '1px',
                        color: 'aliceBlue',
                      }}
                    />
                  )) || <TitlePlaceholder />}

                {shouldShowDescription && isMobile && (
                  <AnimatedInfoButton
                    description={description}
                    tutorialSlide={tutorialSlide}
                    customTutorial={customTutorial}
                  />
                )}
              </Flex>
              {children && !isInAnalyticsMaintenanceMode && (
                <Flex
                  sx={{
                    // width: '100%',
                    ml: [0, '0.8rem'],
                  }}
                >
                  {children}
                </Flex>
              )}
            </Flex>
            {shouldShowDescription && !isMobile && (
              <AnimatedInfoButton
                description={description}
                tutorialSlide={tutorialSlide}
                customTutorial={customTutorial}
              />
            )}
          </Flex>
        </Flex>
      </Flex>
      {rightContent && !isInAnalyticsMaintenanceMode && (
        <Flex sx={{ mr: 0, alignItems: 'center', flexShrink: 0 }}>{rightContent}</Flex>
      )}
    </Flex>
  )
}

export function TitlePlaceholder({ sx }: { sx?: ThemeUIStyleObject }): ReactElement {
  return (
    <Box
      sx={{
        width: ['10rem', '16rem'],
        height: ['1.5rem', '2.4rem'],
        backgroundColor: 'white25',
        borderRadius: '1.2rem',
        opacity: 0.5,
        animation: `${keyframes({ '50%': { opacity: '1' } })} 3s 1s backwards infinite`,
        ...sx,
      }}
    />
  )
}

function AnimatedInfoButton({
  description,
  tutorialSlide = 0,
  customTutorial,
}: {
  description?: string | ReactNode
  tutorialSlide?: number
  customTutorial?: TutorialType
}): ReactElement {
  const { onInfoIconClick } = useTutorialSlides()
  const { wasTutorialViewed } = useTutorialSlides()

  return (
    <Button
      variant="transparent"
      data-testid="header-info-button"
      ariaLabel="view tutorial"
      sx={{
        width: 'fit-content',
        p: 0,
        color: wasTutorialViewed ? 'aliceBlue60' : 'banana',
        gap: '0.4rem',
        height: '1.2rem',
        justifyContent: 'flex-start',
        maxWidth: '100%',
        '&:hover': {
          color: wasTutorialViewed ? 'aliceBlue' : 'banana80',
          '& > .animation-block': {
            backgroundColor: wasTutorialViewed ? 'transparent' : 'banana80',
            'svg path': {
              fill: wasTutorialViewed ? 'aliceBlue' : 'black',
            },
          },
        },
      }}
      onClick={() => onInfoIconClick?.(tutorialSlide, customTutorial)}
    >
      {typeof description === 'string' ? (
        <TextWithOverFlowAndTippyPopup
          variant="size12Weight500"
          testId="header-description"
          content={description}
          ariaLabel={description || ''}
          sxText={{
            display: ['none', 'inline-block'],
          }}
          maxWidth="450px"
        />
      ) : (
        <Flex data-testid="header-description" sx={{ display: ['none', 'flex'] }}>
          {description}
        </Flex>
      )}
      <Flex
        className="animation-block"
        sx={{
          flexShrink: 0,
          p: wasTutorialViewed ? 0 : '0.2rem',
          borderRadius: '100%',
          backgroundColor: wasTutorialViewed ? 'transparent' : 'banana',
          '@-webkit-keyframes rotation': {
            '0%': { boxShadow: '0 0 0 0 rgba(214, 166, 45, 1)' },
            '100%': { boxShadow: '0 0 10px 8px transparent', borderWidth: '2px' },
          },
          WebkitAnimation: 'rotation 2s infinite linear',
          animation: wasTutorialViewed ? 'none' : 'rotation 2s infinite linear',
        }}
      >
        <Svg
          svg={InfoIcon}
          fill={wasTutorialViewed ? 'aliceBlue60' : 'black'}
          sx={{ width: '1.2rem', height: '1.2rem', flexShrink: 0 }}
          descriptiveText="View tutorial information"
        />
      </Flex>
    </Button>
  )
}
